import React, { FC, useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { TrainingCourseDuration } from "@/features/training/model/Training/TrainingCourse";
import type { UpdateTrainingDrillSessionRequest } from "@/features/training/model/Training/TrainingDrillSession";
import type { TrainingDrillSessionSummary } from "@/features/training/model/Training/TrainingDrillSession";
import type {
	TrainingSession,
	TrainingSessionRequest,
	TrainingSessionSummary,
} from "@/features/training/model/Training/TrainingSession";
import { TrainingPlannerTourSteps } from "@/features/training/pages/Planner/TrainingPlannerTourSteps";
import { NewDrillSession } from "@/features/training/pages/drill-tab/panel-right/drill/NewDrillSession";
import { NewCourseSession } from "@/features/training/pages/training-tab/panel-right/training-course/NewCourseSession";
import { FallBackErrorMessage } from "@/features/training/ui";
import { AlertToast, InfoToast } from "@/shared/notifications/AlertToast";
import type {
	DateSelectArg,
	EventClickArg,
	EventDropArg,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import type { EventResizeDoneArg } from "@fullcalendar/interaction";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ListIcon from "@mui/icons-material/List";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import TodayIcon from "@mui/icons-material/Today";
import {
	Box,
	Button,
	ButtonGroup,
	Collapse,
	IconButton,
	InputAdornment,
	LinearProgress,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	ListItemIcon as MuiListItemIcon,
	ListItemText as MuiListItemText,
	TextField,
	Tooltip,
	Typography,
	alpha,
	darken,
	useTheme,
} from "@mui/material";
import { useTour } from "@reactour/tour";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import {
	useMutateUpdateTrainingDrillSession,
	useMutateUpdateTrainingSession,
} from "@/service/TrainingMutations";
import {
	useFetchCourseSessions,
	useFetchDrillSessions,
} from "@/service/TrainingQueries";
import { TrainingQueryKeys } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

type ViewType = "dayGridMonth" | "timeGridWeek" | "timeGridDay";

const isPlannerView = (value: string | null): value is ViewType =>
	value === "dayGridMonth" ||
	value === "timeGridWeek" ||
	value === "timeGridDay";

const DEFAULT_SLOT_MIN_TIME = "00:00:00";
// FullCalendar treats slotMaxTime as an exclusive bound.
const DEFAULT_SLOT_MAX_TIME = "24:00:00";
const DEFAULT_SCROLL_TIME = "08:00:00";

export type PlannerEvent = {
	id: string;
	title: string;
	start: string;
	end: string;
	backgroundColor: string;
	borderColor: string;
	extendedProps: {
		type: "course" | "drill";
		courseId?: number;
		drillId?: number;
		sessionId: number;
	};
	url?: string;
};

type PlannerCourseSession = TrainingSession & {
	CourseId?: number;
	GroupId?: number | null;
};

export const PlannerCalendar: FC = () => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const { timezone } = useTime();
	const isDark = theme.palette.mode === "dark";
	const selectedViewButtonBackground = isDark
		? theme.palette.primary.main
		: theme.palette.primary.dark;
	const selectedViewButtonColor = theme.palette.getContrastText(
		selectedViewButtonBackground,
	);
	const [searchParams, setSearchParams] = useSearchParams();

	const courseColor = theme.palette.primary.main;
	const drillColor = theme.palette.success.main;
	const navigate = useNavigate();
	const [newCourseOpen, setNewCourseOpen] = useState(false);
	const [newDrillOpen, setNewDrillOpen] = useState(false);
	const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(
		null,
	);
	const [createMenuPosition, setCreateMenuPosition] = useState<{
		top: number;
		left: number;
	} | null>(null);
	const [selectedDateForCreate, setSelectedDateForCreate] =
		useState<Date | null>(null);
	const [eventListOpen, setEventListOpen] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const currentView = useMemo<ViewType>(() => {
		const viewParam = searchParams.get("view");
		return isPlannerView(viewParam) ? viewParam : "dayGridMonth";
	}, [searchParams]);
	const currentDate = useMemo(() => {
		const dateParam = searchParams.get("date");
		const parsed = dateParam ? dayjs(dateParam) : dayjs();
		return parsed.isValid() ? parsed : dayjs();
	}, [searchParams]);

	const queryClient = useQueryClient();
	const updateCourseSession = useMutateUpdateTrainingSession();
	const { updateTrainingDrillSession } = useMutateUpdateTrainingDrillSession();

	const startDate = currentDate
		.subtract(1, "month")
		.startOf("month")
		.format("YYYY-MM-DD");
	const endDate = currentDate
		.add(1, "month")
		.endOf("month")
		.format("YYYY-MM-DD");
	const queryOptions = { StartDate: startDate, EndDate: endDate };

	const { data: courseSessionsData, isLoading: loadingCourse } =
		useFetchCourseSessions(queryOptions, true);
	const { data: drillSessionsData, isLoading: loadingDrill } =
		useFetchDrillSessions(queryOptions, true);
	const courseSessions: TrainingSessionSummary[] = courseSessionsData ?? [];
	const drillSessions: TrainingDrillSessionSummary[] = drillSessionsData ?? [];

	const events: PlannerEvent[] = useMemo(() => {
		const items: PlannerEvent[] = [];
		const addHours = (d: Date, h: number) =>
			new Date(d.getTime() + h * 60 * 60 * 1000);

		courseSessions.forEach((s) => {
			const start = dayjs.utc(s.Date).toDate();
			const end = addHours(start, 1);
			items.push({
				id: `course-${s.Id}`,
				title: s.Name || "Course Session",
				start: start.toISOString(),
				end: end.toISOString(),
				backgroundColor: courseColor,
				borderColor: courseColor,
				extendedProps: {
					type: "course",
					courseId: s.CourseId,
					sessionId: s.Id,
				},
				url: `/training/courses/${s.CourseId}/sessions/${s.Id}`,
			});
		});

		drillSessions.forEach((s) => {
			const start = dayjs.utc(s.Date).toDate();
			const end = addHours(start, 1);
			items.push({
				id: `drill-${s.Id}`,
				title: s.Name || "Drill Session",
				start: start.toISOString(),
				end: end.toISOString(),
				backgroundColor: drillColor,
				borderColor: drillColor,
				extendedProps: { type: "drill", drillId: s.DrillId, sessionId: s.Id },
				url: `/training/drills/${s.DrillId}/sessions/${s.Id}`,
			});
		});

		return items;
	}, [courseSessions, drillSessions, courseColor, drillColor]);

	const filteredEvents = useMemo(() => {
		if (!searchQuery.trim()) return events;
		const q = searchQuery.toLowerCase().trim();
		return events.filter(
			(e) =>
				e.title.toLowerCase().includes(q) ||
				dayjs
					.utc(e.start)
					.tz(timezone)
					.format("DD/MM/YY HH:mm:ss")
					.toLowerCase()
					.includes(q),
		);
	}, [events, searchQuery, timezone]);

	const visibleRange = useMemo(() => {
		if (currentView === "timeGridDay") {
			return {
				start: currentDate.startOf("day"),
				end: currentDate.endOf("day"),
			};
		}

		if (currentView === "timeGridWeek") {
			return {
				start: currentDate.startOf("week"),
				end: currentDate.endOf("week"),
			};
		}

		return {
			start: currentDate.startOf("month"),
			end: currentDate.endOf("month"),
		};
	}, [currentDate, currentView]);

	const visibleEvents = useMemo(
		() =>
			filteredEvents.filter((event) => {
				const eventStart = dayjs.utc(event.start).tz(timezone).valueOf();
				const eventEnd = dayjs.utc(event.end).tz(timezone).valueOf();
				const rangeStart = visibleRange.start.valueOf();
				const rangeEnd = visibleRange.end.valueOf();

				return eventStart <= rangeEnd && eventEnd >= rangeStart;
			}),
		[filteredEvents, timezone, visibleRange],
	);
	const dayHeaderFormat = useMemo(() => {
		if (currentView === "timeGridDay") {
			return {
				weekday: "long",
				day: "2-digit",
				month: "short",
			} as const;
		}

		if (currentView === "timeGridWeek") {
			return {
				weekday: "short",
				day: "2-digit",
				month: "short",
			} as const;
		}

		return {
			weekday: "short",
			day: "numeric",
		} as const;
	}, [currentView]);

	const handleDateSelect = useCallback((selectInfo: DateSelectArg) => {
		setSelectedDateForCreate(selectInfo.start);
		setCreateMenuPosition({
			top: selectInfo.jsEvent?.clientY ?? 0,
			left: selectInfo.jsEvent?.clientX ?? 0,
		});
		setCreateMenuAnchor(null);
	}, []);

	const openCreateMenu = (e: React.MouseEvent<HTMLElement>) => {
		setCreateMenuAnchor(e.currentTarget);
		setCreateMenuPosition(null);
		setSelectedDateForCreate(null); // Will use "today" when picking from Create button
	};
	const closeCreateMenu = () => {
		setCreateMenuAnchor(null);
		setCreateMenuPosition(null);
	};
	const handleCreateCourse = () => {
		setSelectedDateForCreate((prev) => prev ?? new Date());
		setNewCourseOpen(true);
		closeCreateMenu();
	};
	const handleCreateDrill = () => {
		setSelectedDateForCreate((prev) => prev ?? new Date());
		setNewDrillOpen(true);
		closeCreateMenu();
	};

	const handleEventClick = useCallback(
		(info: EventClickArg) => {
			info.jsEvent.preventDefault();
			const eventData = events.find((e) => e.id === info.event.id);
			if (eventData?.url) navigate(eventData.url);
		},
		[navigate, events],
	);

	const handleEventDrop = useCallback(
		async (info: EventDropArg) => {
			const eventProps = info.event
				.extendedProps as PlannerEvent["extendedProps"];
			const newStart = info.event.start!;

			try {
				if (eventProps.type === "course") {
					const session = (await SATOSPortal.GetCourseSession(
						eventProps.sessionId,
					)) as PlannerCourseSession;
					const duration = session.Duration as
						| TrainingCourseDuration
						| undefined;
					const req: TrainingSessionRequest = {
						CourseId: session.CourseId ?? eventProps.courseId ?? 0,
						Information: session.Information,
						SessionDate: newStart,
						ExpiresDate:
							duration === TrainingCourseDuration.Custom
								? session.ExpiresDate
								: null,
						TrainerId: session.Trainer?.Id ?? 0,
						StudentIds: (session.Students ?? []).map((s) => s.Id),
						GroupId: session.GroupId ?? null,
					};
					await updateCourseSession({
						sessionId: eventProps.sessionId,
						request: req,
					});
				} else {
					const session = await SATOSPortal.GetDrillSession(
						eventProps.sessionId,
					);
					const req: UpdateTrainingDrillSessionRequest = {
						Id: eventProps.sessionId,
						Date: newStart.toISOString(),
						ResponsibleId: session.Responsible?.Id ?? 0,
						Information: session.Information,
						Comment: session.Comment ?? "",
						KpiParameters:
							session.KpiParameters?.map((k) => ({
								Id: k.Id,
								Value: k.Value,
							})) ?? [],
					};
					await updateTrainingDrillSession(req);
				}
				await queryClient.invalidateQueries({
					queryKey: [TrainingQueryKeys.courseSessions],
				});
				await queryClient.invalidateQueries({
					queryKey: [TrainingQueryKeys.drillSessions],
				});
				void AlertToast.success();
			} catch (err) {
				info.revert();
				void AlertToast.failed();
				console.error(err);
			}
		},
		[updateCourseSession, updateTrainingDrillSession, queryClient],
	);

	const handleEventResize = useCallback((info: EventResizeDoneArg) => {
		info.revert();
		InfoToast(
			"Duration changes are not saved. Edit the session for more options.",
		);
	}, []);

	const updatePlannerLocation = useCallback(
		(next: { view?: ViewType; date?: dayjs.Dayjs }) => {
			const nextParams = new URLSearchParams(searchParams);
			nextParams.set("view", next.view ?? currentView);
			nextParams.set("date", (next.date ?? currentDate).format("YYYY-MM-DD"));
			setSearchParams(nextParams);
		},
		[currentDate, currentView, searchParams, setSearchParams],
	);

	const navUnit =
		currentView === "dayGridMonth"
			? "month"
			: currentView === "timeGridDay"
				? "day"
				: "week";
	const navPrev = () =>
		updatePlannerLocation({ date: currentDate.subtract(1, navUnit) });
	const navNext = () =>
		updatePlannerLocation({ date: currentDate.add(1, navUnit) });
	const goToday = () => updatePlannerLocation({ date: dayjs().tz(timezone) });

	const isLoading = loadingCourse || loadingDrill;
	const { setSteps, setCurrentStep } = useTour();

	useEffect(() => {
		setCurrentStep(0);
		if (setSteps) setSteps(TrainingPlannerTourSteps);
	}, [setSteps, setCurrentStep]);

	useEffect(() => {
		const dateParam = searchParams.get("date");
		const viewParam = searchParams.get("view");
		if (dateParam && isPlannerView(viewParam)) {
			return;
		}
		updatePlannerLocation({});
	}, [searchParams, updatePlannerLocation]);

	if (isLoading && !events.length) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	return (
		<Sentry.ErrorBoundary fallback={<FallBackErrorMessage />}>
			<Box
				sx={{
					position: "relative",
					display: "flex",
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					bgcolor: "background.default",
				}}
			>
				{/* Sidebar: Event list + search */}
				<Collapse in={eventListOpen} orientation="horizontal">
					<Box
						data-tour="training-planner-sidebar"
						sx={{
							position: "absolute",
							left: 0,
							top: 0,
							bottom: 0,
							width: 336,
							minWidth: 280,
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
							bgcolor: "background.paper",
							zIndex: 1,
							border: 1,
							borderColor: "divider",
							borderRadius: 3,
							boxShadow: isDark
								? "0 1px 0 rgba(255,255,255,0.04)"
								: "0 1px 2px rgba(15,23,42,0.04)",
						}}
					>
						<Box
							sx={(t) => ({
								px: 2.25,
								py: 1,
								borderBottom: 1,
								borderColor: alpha(t.palette.divider, 0.85),
								flexShrink: 0,
								bgcolor:
									t.palette.mode === "dark"
										? alpha(t.palette.common.white, 0.02)
										: "grey.50",
							})}
						>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									mb: 0.05,
									color: "primary.main",
									fontWeight: 700,
									letterSpacing: "0.08em",
									textTransform: "uppercase",
								}}
							>
								<Translate String="Auto.Features.Training.Pages.Planner.PlannerCalendar.L506C9.Sessions">Sessions</Translate>
							</Typography>
							<Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.05 }}>
								<Translate String="Auto.Features.Training.Pages.Planner.PlannerCalendar.L509C83.UpcomingSessions">Upcoming sessions</Translate>
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, mb: 1 }}>
								{visibleEvents.length} <Translate String="Auto.Features.Training.Pages.Planner.PlannerCalendar.L513C31.Session">session</Translate>{visibleEvents.length === 1 ? "" : "s"} <Translate String="Auto.Features.Training.Pages.Planner.PlannerCalendar.L513C78.InView">in view</Translate>
							</Typography>
							<TextField
								size="small"
								placeholder={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Planner.PlannerCalendar.L517C21.SearchSessions", "Search sessions…")}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								fullWidth
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: 2,
										bgcolor: "background.paper",
									},
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon fontSize="small" color="action" />
										</InputAdornment>
									),
								}}
							/>
						</Box>
						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								overflowY: "auto",
								overflowX: "hidden",
								py: 1,
							}}
						>
							<List
								disablePadding
								sx={{ py: 0, px: 1.25, "& > * + *": { mt: 0.75 } }}
							>
								{visibleEvents.length === 0 ? (
									<Box sx={{ p: 2, textAlign: "center" }}>
										<Typography variant="body2" color="text.secondary">
											{searchQuery
												? "No matching sessions"
												: "No sessions in this range"}
										</Typography>
									</Box>
								) : (
									visibleEvents
										.sort(
											(a, b) =>
												new Date(a.start).getTime() -
												new Date(b.start).getTime(),
										)
										.map((ev) => (
											<ListItemButton
												key={ev.id}
												onClick={() => ev.url && navigate(ev.url)}
												sx={{
													py: 0.75,
													px: 1.25,
													borderRadius: 2,
													borderLeft: 2.5,
													borderLeftColor:
														ev.extendedProps.type === "course"
															? courseColor
															: drillColor,
													bgcolor: "transparent",
													transition: "all 0.15s ease",
													"&:hover": {
														bgcolor: "action.hover",
														transform: "translateX(2px)",
													},
												}}
											>
												<ListItemIcon sx={{ minWidth: 28, mr: 1 }}>
													{ev.extendedProps.type === "course" ? (
														<SchoolIcon
															sx={{ color: "primary.main", fontSize: 16 }}
														/>
													) : (
														<FitnessCenterIcon
															sx={{ color: "success.main", fontSize: 16 }}
														/>
													)}
												</ListItemIcon>
												<ListItemText
													primary={
														<Typography variant="body2" fontWeight={600} noWrap>
															{ev.title}
														</Typography>
													}
													secondary={
														<Typography
															variant="caption"
															color="text.secondary"
														>
															{dayjs
																.utc(ev.start)
																.tz(timezone)
																.format("DD/MM/YY HH:mm:ss")}
														</Typography>
													}
													primaryTypographyProps={{ sx: { lineHeight: 1.3 } }}
													secondaryTypographyProps={{
														sx: { mt: 0.25, lineHeight: 1.2 },
													}}
												/>
											</ListItemButton>
										))
								)}
							</List>
						</Box>
					</Box>
				</Collapse>

				{/* Main: Calendar - add left margin when sidebar is open */}
				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						ml: eventListOpen ? "352px" : 0,
						transition: "margin-left 300ms",
					}}
				>
					{/* Header */}
					<Box
						data-tour="training-planner-header"
						sx={(t) => ({
							flexShrink: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: 1.5,
							px: { xs: 2, sm: 2.5 },
							py: 1.1,
							border: 1,
							borderColor: alpha(t.palette.divider, 0.85),
							bgcolor: "background.paper",
							borderRadius: 3,
							boxShadow:
								t.palette.mode === "light"
									? "0 1px 2px rgba(15,23,42,0.04)"
									: "none",
						})}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", minWidth: 0 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.5,
									ml: { xs: 0, md: 1 },
									p: 0.35,
									borderRadius: 2,
									border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
									bgcolor:
										theme.palette.mode === "dark"
											? alpha(theme.palette.common.white, 0.02)
											: "grey.50",
								}}
							>
								<IconButton
									size="small"
									onClick={() => setEventListOpen(!eventListOpen)}
									aria-label={eventListOpen ? "Hide list" : "Show list"}
									sx={{ borderRadius: 2 }}
								>
									<ListIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={navPrev}
									aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Planner.PlannerCalendar.L688C21.Previous", "Previous")}
									sx={{ borderRadius: 2 }}
								>
									<ChevronLeftIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={navNext}
									aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Planner.PlannerCalendar.L696C21.Next", "Next")}
									sx={{ borderRadius: 2 }}
								>
									<ChevronRightIcon fontSize="small" />
								</IconButton>
								<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Planner.PlannerCalendar.L701C24.GoToToday", "Go to today")}>
									<Button
										size="small"
										variant="outlined"
										onClick={goToday}
										startIcon={<TodayIcon />}
										sx={{
											textTransform: "none",
											borderRadius: 2,
											borderColor: "divider",
											color: "text.primary",
										}}
									>
										<Translate String="Auto.Features.Training.Pages.Planner.PlannerCalendar.L713C11.Today">Today</Translate>
									</Button>
								</Tooltip>
							</Box>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
							<ButtonGroup
								size="small"
								variant="outlined"
								sx={{
									borderRadius: 2,
									"& .MuiButton-root": {
										textTransform: "none",
										borderRadius: 2,
									},
								}}
							>
								{(
									[
										{
											key: "dayGridMonth",
											label: "Month",
											icon: CalendarMonthIcon,
										},
										{ key: "timeGridWeek", label: "Week", icon: null },
										{ key: "timeGridDay", label: "Day", icon: null },
									] as const
								).map(({ key, label }) => (
									<Button
										key={key}
										onClick={() => updatePlannerLocation({ view: key })}
										variant={currentView === key ? "contained" : "outlined"}
										sx={{
											bgcolor:
												currentView === key
													? selectedViewButtonBackground
													: "transparent",
											color:
												currentView === key
													? selectedViewButtonColor
													: "text.secondary",
											"&:hover": {
												bgcolor:
													currentView === key
														? darken(selectedViewButtonBackground, 0.08)
														: alpha(
																theme.palette.primary.main,
																isDark ? 0.2 : 0.08,
															),
												color:
													currentView === key
														? theme.palette.getContrastText(
																darken(selectedViewButtonBackground, 0.08),
															)
														: "primary.main",
											},
										}}
									>
										{label}
									</Button>
								))}
							</ButtonGroup>

							<ButtonGroup
								variant="outlined"
								sx={{
									ml: 1,
									"& .MuiButton-root": {
										textTransform: "none",
										borderColor: "divider",
										color: "text.primary",
										bgcolor: "background.paper",
										fontWeight: 600,
										"&:hover": {
											borderColor: "primary.main",
											bgcolor: alpha(
												theme.palette.primary.main,
												isDark ? 0.16 : 0.06,
											),
										},
									},
								}}
							>
								<Button
									onClick={openCreateMenu}
									sx={{
										borderRadius: "8px 0 0 8px",
									}}
								>
									<AddIcon sx={{ mr: 0.5, fontSize: 18 }} />
									<Translate String="Training.Planner.Actions.Create">
										Create
									</Translate>
								</Button>
								<Button
									size="small"
									onClick={openCreateMenu}
									aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Planner.PlannerCalendar.L812C21.CreateOptions", "Create options")}
									sx={{ px: 0.5, borderRadius: "0 8px 8px 0", minWidth: 32 }}
								>
									<ArrowDropDownIcon />
								</Button>
							</ButtonGroup>
							<Menu
								anchorEl={createMenuAnchor}
								anchorReference={
									createMenuPosition ? "anchorPosition" : "anchorEl"
								}
								anchorPosition={createMenuPosition ?? undefined}
								open={Boolean(createMenuAnchor) || Boolean(createMenuPosition)}
								onClose={closeCreateMenu}
								anchorOrigin={
									createMenuPosition
										? { vertical: "top", horizontal: "left" }
										: { vertical: "bottom", horizontal: "right" }
								}
								transformOrigin={
									createMenuPosition
										? { vertical: "top", horizontal: "left" }
										: { vertical: "top", horizontal: "right" }
								}
								slotProps={{ paper: { sx: { mt: 1.5, borderRadius: 2 } } }}
							>
								<MenuItem onClick={handleCreateCourse} sx={{ py: 1.5 }}>
									<MuiListItemIcon>
										<SchoolIcon color="primary" fontSize="small" />
									</MuiListItemIcon>
									<MuiListItemText
										primary={
											<Translate String="Training.Planner.Actions.NewCourseSession">
												New Course Session
											</Translate>
										}
										secondary={
											<Typography variant="caption" color="text.secondary">
												<Translate String="Training.Planner.Actions.NewCourseSession.Desc">
													Schedule a session for a course
												</Translate>
											</Typography>
										}
									/>
								</MenuItem>
								<MenuItem onClick={handleCreateDrill} sx={{ py: 1.5 }}>
									<MuiListItemIcon>
										<FitnessCenterIcon color="success" fontSize="small" />
									</MuiListItemIcon>
									<MuiListItemText
										primary={
											<Translate String="Training.Planner.Actions.NewDrillSession">
												New Drill Session
											</Translate>
										}
										secondary={
											<Typography variant="caption" color="text.secondary">
												<Translate String="Training.Planner.Actions.NewDrillSession.Desc">
													Schedule a session for a drill
												</Translate>
											</Typography>
										}
									/>
								</MenuItem>
							</Menu>
						</Box>
					</Box>

					{/* Calendar area */}
					<Box
						data-tour="training-planner-calendar"
						sx={{
							flex: 1,
							minHeight: 0,
							pt: 1.5,
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box
							sx={{
								flex: 1,
								minHeight: 420,
								minWidth: 0,
								"& .fc": {
									"--fc-border-color": theme.palette.divider,
									"--fc-page-bg-color": theme.palette.background.paper,
									"--fc-neutral-bg-color": theme.palette.background.paper,
									"--fc-neutral-text-color": theme.palette.text.secondary,
									"--fc-today-bg-color": alpha(
										theme.palette.primary.main,
										isDark ? 0.2 : 0.08,
									),
									"--fc-now-indicator-color": theme.palette.error.main,
									"--fc-list-event-hover-bg-color": theme.palette.action.hover,
									fontFamily: theme.typography.fontFamily,
								},
								"& .fc-theme-standard": {
									borderRadius: 3,
									overflow: "hidden",
									boxShadow: isDark
										? "0 2px 12px rgba(0,0,0,0.3)"
										: "0 2px 12px rgba(0,0,0,0.08)",
									border: "1px solid",
									borderColor: "divider",
								},
								"& .fc-scrollgrid": { border: "none" },
								"& .fc-col-header-cell": {
									padding: "12px 10px",
									background: alpha(
										theme.palette.primary.main,
										isDark ? 0.15 : 0.06,
									),
									fontSize: theme.typography.caption.fontSize,
									fontWeight: 700,
									color: "text.secondary",
									textTransform: "uppercase",
									letterSpacing: 0.5,
								},
								"& .fc-daygrid-day-number": {
									padding: "8px",
									fontSize: theme.typography.body2.fontSize,
									fontWeight: 500,
									color: "text.secondary",
									borderRadius: "50%",
									width: 32,
									height: 32,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								},
								"& .fc-day-today .fc-daygrid-day-number": {
									bgcolor: "primary.main",
									color: "primary.contrastText",
									fontWeight: 700,
								},
								"& .fc-daygrid-day": {
									bgcolor: "background.paper",
								},
								"& .fc-timegrid-slot": {
									height: 52,
									bgcolor: "background.paper",
								},
								"& .fc-timegrid-slot-label-cushion": {
									paddingTop: 6,
									paddingBottom: 6,
								},
								"& .fc-event": {
									borderRadius: 6,
									border: "none",
									padding: "3px 8px",
									fontSize: theme.typography.body2.fontSize,
									cursor: "pointer",
									boxShadow: isDark
										? "0 1px 3px rgba(0,0,0,0.4)"
										: "0 1px 3px rgba(0,0,0,0.12)",
									transition: "transform 0.15s ease, box-shadow 0.15s ease",
								},
								"& .fc-event:hover": {
									transform: "translateY(-1px)",
									boxShadow: isDark
										? "0 4px 12px rgba(0,0,0,0.5)"
										: "0 4px 12px rgba(0,0,0,0.18)",
								},
								"& .fc-event-dragging": {
									opacity: 0.9,
									boxShadow: isDark
										? "0 8px 24px rgba(0,0,0,0.6)"
										: "0 8px 24px rgba(0,0,0,0.25)",
								},
								"& .fc-timegrid-axis": {
									fontSize: theme.typography.caption.fontSize,
									color: "text.secondary",
									fontWeight: 500,
								},
								"& .fc-timegrid-slot-label": {
									fontSize: theme.typography.caption.fontSize,
									color: "text.secondary",
								},
							}}
						>
							<FullCalendar
								key={`${currentView}-${currentDate.format("YYYY-MM-DD")}-${timezone}`}
								plugins={[
									momentTimezonePlugin,
									dayGridPlugin,
									timeGridPlugin,
									interactionPlugin,
								]}
								initialView={currentView}
								initialDate={currentDate.toISOString()}
								timeZone={timezone}
								headerToolbar={false}
								events={events}
								editable={true}
								eventStartEditable={true}
								eventDurationEditable={true}
								selectable={true}
								selectMirror={true}
								dayMaxEvents={4}
								nowIndicator={true}
								dayHeaderFormat={dayHeaderFormat}
								slotMinTime={DEFAULT_SLOT_MIN_TIME}
								slotMaxTime={DEFAULT_SLOT_MAX_TIME}
								scrollTime={DEFAULT_SCROLL_TIME}
								allDaySlot={false}
								height="100%"
								select={handleDateSelect}
								eventClick={handleEventClick}
								eventDrop={(dropInfo) => {
									void handleEventDrop(dropInfo);
								}}
								eventResize={handleEventResize}
							/>
						</Box>
					</Box>
				</Box>

				<NewCourseSession
					open={newCourseOpen}
					handleClose={() => {
						setNewCourseOpen(false);
						setSelectedDateForCreate(null);
					}}
					selectedDate={selectedDateForCreate ?? undefined}
				/>
				<NewDrillSession
					open={newDrillOpen}
					handleClose={() => {
						setNewDrillOpen(false);
						setSelectedDateForCreate(null);
					}}
					selectedDate={selectedDateForCreate ?? undefined}
				/>
			</Box>
		</Sentry.ErrorBoundary>
	);
};
